import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Lessons() {
  const [lessons, setLessons] = useState([]);
  const [form, setForm] = useState({ title: "", topic: "", body: "", points_reward: 5 });
  const [aiTopic, setAiTopic] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/lessons").then((r) => setLessons(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/lessons", form);
      toast.success("Lesson added");
      setForm({ title: "", topic: "", body: "", points_reward: 5 });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const generateAI = async () => {
    if (!aiTopic.trim()) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("topic", aiTopic);
      await api.post("/lessons/generate", fd);
      toast.success("AI lesson drafted via GPT-5.2");
      setAiTopic("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Generation failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    await api.delete(`/lessons/${id}`);
    toast.success("Lesson removed");
    load();
  };

  return (
    <div className="p-8 space-y-6" data-testid="lessons-page">
      <header>
        <div className="overline">// daily · learning · engine</div>
        <h1 className="font-display font-black text-3xl mt-1">Daily Lessons</h1>
        <p className="text-zinc-500 text-sm mt-1">Push one micro-lesson per day to every mechanic. Completion rewards +5 pts.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Manual add */}
        <form onSubmit={submit} className="gs-card p-5 space-y-3" data-testid="lesson-form">
          <div className="overline">New Lesson · Manual</div>
          <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Title (e.g. What is ADAS?)"
            data-testid="lesson-title-input"
            className="w-full bg-[#090A0B] border border-zinc-800 px-3 py-2 text-sm outline-none focus:border-amber-500" />
          <input required value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })}
            placeholder="Topic (e.g. ADAS, P0300, EV)"
            data-testid="lesson-topic-input"
            className="w-full bg-[#090A0B] border border-zinc-800 px-3 py-2 text-sm outline-none focus:border-amber-500 font-mono-data" />
          <textarea required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
            placeholder="Lesson body — 4-6 sentences, mechanic-friendly..."
            rows={5} data-testid="lesson-body-input"
            className="w-full bg-[#090A0B] border border-zinc-800 px-3 py-2 text-sm outline-none focus:border-amber-500" />
          <button type="submit" data-testid="lesson-submit-btn"
            className="bg-amber-500 hover:bg-amber-600 text-black font-display font-bold uppercase tracking-wider px-4 py-2 text-xs flex items-center gap-2">
            <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> Add Lesson
          </button>
        </form>

        {/* AI generate */}
        <div className="gs-card p-5 space-y-3">
          <div className="overline flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-blue-400" /> AI · Generate with GPT-5.2
          </div>
          <p className="text-xs text-zinc-500">Type a topic and let GPT-5.2 draft a complete lesson in mechanic-friendly Hinglish.</p>
          <input value={aiTopic} onChange={(e) => setAiTopic(e.target.value)}
            placeholder="e.g. CAN bus basics"
            data-testid="ai-lesson-topic-input"
            className="w-full bg-[#090A0B] border border-zinc-800 px-3 py-2 text-sm outline-none focus:border-amber-500" />
          <button disabled={busy || !aiTopic.trim()} onClick={generateAI}
            data-testid="ai-lesson-generate-btn"
            className="bg-blue-500 hover:bg-blue-600 text-white font-display font-bold uppercase tracking-wider px-4 py-2 text-xs flex items-center gap-2 disabled:opacity-50">
            <Sparkles className="w-3.5 h-3.5" /> {busy ? "Drafting..." : "Generate"}
          </button>
        </div>
      </div>

      <div>
        <div className="overline mb-3">All Lessons ({lessons.length})</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {lessons.map((l) => (
            <div key={l.id} className="gs-card p-4" data-testid={`lesson-card-${l.id}`}>
              <div className="flex justify-between items-start gap-2">
                <div>
                  <div className="text-sm font-display font-bold text-zinc-100">{l.title}</div>
                  <div className="text-[11px] text-amber-500/80 font-mono-data mt-0.5">#{l.topic} · +{l.points_reward} pts</div>
                </div>
                <button onClick={() => del(l.id)} data-testid={`lesson-delete-${l.id}`}
                  className="text-zinc-600 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="text-xs text-zinc-400 mt-2 leading-relaxed">{l.body}</div>
            </div>
          ))}
          {lessons.length === 0 && <div className="text-zinc-600 text-sm">No lessons yet.</div>}
        </div>
      </div>
    </div>
  );
}
